import 'whatwg-fetch';
import polygonCalculator from 'point-in-polygon';
import proj4 from 'proj4';
import { mapLocation } from './mapLocation';
import { Helper } from '../../../js/util/helper';
import marker from './location.png';
import markerHover from './location_hover.png';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import HighchartsMapModule from 'highcharts/modules/map';
import HighchartsDrilldownModule from 'highcharts/modules/drilldown';

import Worker from './map.worker.js';

const worker = new Worker();

if ( typeof window !== 'undefined' ) {
  window.proj4 = window.proj4 || proj4;
}

HighchartsMapModule( Highcharts );
HighchartsDrilldownModule( Highcharts );
Exporting( Highcharts );

window.Highcharts = Highcharts;

let countryData = null;
let worldData = null;
let longLatsData = null;
let lang = null;

// from CDN https://code.highcharts.com/mapdata/;
let geoJsonBaseUrl = '/themes/custom/mubeacom/build/json/';
if ( document.location.href.indexOf( 'localhost' ) !== -1 || document.location.href.indexOf( '10.0.2.2' ) !== -1 ) {
  geoJsonBaseUrl = '/json/';
}

export default class Map {
  constructor( node ) {
    this.node = node;
    this.addChartContainer();
    window.addEventListener( 'touchstart', function onFirstTouch() {
      document.querySelectorAll( '.map' ).forEach(( item ) => {
        item.classList.add( 'map--touch' );
      });
      window.removeEventListener( 'touchstart', onFirstTouch, false );
    });
    if ( countryData === null ) {
      this.preloadData()
        .then(() => {
          longLatsData = JSON.parse( document.getElementsByClassName( 'map' )[ 0 ].getAttribute( 'data-locations' ));
          lang = JSON.parse( document.getElementsByClassName( 'map' )[ 0 ].getAttribute( 'data-lang' ));
          this.addMap();
        })
        .catch(( error ) => {
          console.log( error );
        });
    } else {
      this.addMap();
    }
  }

  addChartContainer() {
    this.chartContainer = document.createElement( 'DIV' );
    this.chartContainer.classList.add( 'map__container' );
    this.node.appendChild( this.chartContainer );
  }

  preloadData() {

    let jsonNames = [
      'world-palestine-highres',
      'world-continents',
    ];

    return Promise.all(
      jsonNames.map(( jsonName ) => window.fetch( geoJsonBaseUrl + jsonName + '.geo.json', { 'credentials': 'same-origin' }))
    )
      .then(( res ) => {
        return Promise.all( res.map(( r ) => r.json()));
      })
      .then(( jsonData ) => {
        Highcharts.maps[ 'custom/world-palestine-highres' ] = jsonData [ 0 ];
        countryData = Highcharts.geojson( Highcharts.maps[ 'custom/world-palestine-highres' ]);
        Highcharts.maps[ 'custom/world-continents' ] = jsonData[ 1 ];
        worldData = Highcharts.geojson( Highcharts.maps[ 'custom/world-continents' ]);
        return;
      });
  }

  addMap() {
    let chart = Highcharts.mapChart( this.chartContainer, {
      chart: {
        animation: false,
        backgroundColor: 'transparent',
        margin: [ 0, 0, 0, 0 ],
        padding: [ 0, 0, 0, 0 ],
        panning: true,

        events: {
          load: function() {
            if ( document.querySelector( '.map--touch' )) {
              chart.tooltip.options.enabled = false;
            }
          },
          drilldown: function( e ) {
            if ( e.seriesOptions ) {
              return;
            }

            let chart = this;
            let key = e.point.drilldown;
            let shortUrl = `${key}`;

            let pointWithLatLon = function( point, latLon, img, copytext, countryName, tooltip, buttons ) {
              return Highcharts.merge( point, chart.transformFromLatLon( latLon,
                Highcharts.maps[ shortUrl ][ 'hc-transform' ][ 'default' ]), img, copytext, countryName, tooltip, buttons );
            };

            let disableAllPoints = function() {
              let points = chart.series[ 1 ].points;
              for ( let point of points ) {
                point.update({
                  isActive: false,
                  marker: {
                    height: 36,
                    symbol: `url(${ marker })`,
                    width: 30
                  }
                });
              }
            };

            let addDrilldownMap = function() {
              let worldCities = [];
              let continentCities = [];
              for ( let city of longLatsData ) {
                for ( let cityContinent of countryViewCities ) {
                  if ( city.name === cityContinent.name ) {
                    city.continent = cityContinent.continent;
                    city.countryName = cityContinent.countryName;
                    break;
                  }
                }
                worldCities.push( pointWithLatLon({ name: city.name }, { lat: city.latitude, lon: city.longitude }, { img: city.image }, { copytext: city.copytext }, { countryName: city.countryName }, { tooltip: true }, { buttons: city.buttons }));
              }

              let dataMap = Highcharts.geojson( Highcharts.maps[ shortUrl ]);
              let taiwanData = {};
              let taiwanIndex = -1;
              let taiwanSet = false;
              let chinaData = {};
              let chinaIndex = -1;
              let chinaSet = false;

              dataMap.forEach( function( item, index ) {
                item.color = '#DBE5F0';
                item.borderWidth = 0;
                item.value = 0;
                item.tooltip = false;

                if ( item.name === 'Taiwan' ) {
                  taiwanIndex = index;
                  if ( taiwanData.color ) {
                    item.color = taiwanData.color;
                    item.borderWidth = taiwanData.borderWidth;
                    item.borderColor = taiwanData.borderColor;
                    item.labelName = taiwanData.labelName;
                    item.tooltip = taiwanData.tooltip;
                  }
                }
                if ( item.name === 'China' ) {
                  chinaData = item;
                  chinaIndex = index;
                }

                for ( let city of worldCities ) {
                  if ( item.name === city.countryName ) {
                    if ( shortUrl === 'south-america' && item.name === 'United Kingdom' ) {
                      // do nothing
                    } else if ( shortUrl === 'europe' && city.x > 9732 ) {
                      // do nothing
                    } else {
                      item.color = {
                        radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
                        stops: [
                          [ 0, '#0096C1' ],
                          [ 1, '#004B95' ]
                        ]
                      };
                      item.borderWidth = 0;
                      item.borderColor = '#DBE5F0';
                      item.value += 1;
                      item.labelName = checkLangCountry( item.name );
                      item.tooltip = true;
                      if ( item.name === 'China' ) {
                        chinaSet = true;
                        taiwanData.color = {
                          radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
                          stops: [
                            [ 0, '#0096C1' ],
                            [ 1, '#004B95' ]
                          ]
                        };
                        taiwanData.name = item.name;
                        taiwanData.borderWidth = 0;
                        taiwanData.borderColor = '#DBE5F0';
                        taiwanData.labelName = checkLangCountry( item.name );
                        taiwanData.tooltip = true;
                      }
                      if ( item.name === 'Taiwan' ) {
                        taiwanSet = true;
                        if ( !chinaSet ) {
                          chinaData.color = {
                            radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
                            stops: [
                              [ 0, '#0096C1' ],
                              [ 1, '#004B95' ]
                            ]
                          };
                          chinaData.borderWidth = 0;
                          chinaData.borderColor = '#DBE5F0';
                          chinaData.labelName = checkLangCountry( 'China' );
                          chinaData.tooltip = true;
                          chinaData.value = item.value;
                        }
                      }
                      continentCities.push( city );
                    }
                  }
                }
              });
              if ( chinaIndex !== -1 && !chinaSet ) {
                dataMap[ chinaIndex ] = chinaData;
              } else if ( chinaIndex !== -1 && chinaSet && taiwanSet ) {
                dataMap[ chinaIndex ].value = dataMap[ chinaIndex ].value + dataMap[ taiwanIndex ].value;
              }
              if ( taiwanIndex !== -1 && ( taiwanSet || chinaSet )) {
                dataMap[ taiwanIndex ].name = 'China';
                dataMap[ taiwanIndex ].labelName = checkLangCountry( 'China' );
                dataMap[ taiwanIndex ].value = dataMap[ chinaIndex ].value;
              }
              mapLocation.createMobileOverview( key, continentCities, lang, chart );

              chart.addSeriesAsDrilldown( e.point, {
                name: e.point.name,
                data: dataMap,
                states: {
                  hover: {
                    enabled: false
                  }
                }
              });

              chart.addSeries({
                id: 'cities',
                name: 'Cities',
                type: 'mappoint',
                color: 'white',
                marker: {
                  height: 36,
                  symbol: `url(${ marker })`,
                  width: 30
                },
                data: continentCities,
                dataLabels: {
                  enabled: false
                },
                point: {
                  events: {
                    click: function() {
                      let hadOverlayBefore = document.querySelector( '.map__location-container--show' ) !== null;
                      mapLocation.createLocationInformation( this, continentCities, chart, handleResize );
                      if ( !hadOverlayBefore ) {
                        handleResize();
                      }
                      disableAllPoints();
                      this.update({
                        isActive: true,
                        marker: {
                          height: 36,
                          symbol: `url(${ markerHover })`,
                          width: 30
                        }
                      });
                    },
                    mouseOver: function() {
                      if ( document.querySelector( '.map--touch' )) {
                        // do nothing
                      } else {
                        this.update({
                          marker: {
                            height: 36,
                            symbol: `url(${ markerHover })`,
                            width: 30
                          }
                        });
                      }
                    },
                    mouseOut: function() {
                      if ( document.querySelector( '.map--touch' )) {
                        // do nothing
                      } else {
                        if ( !this.isActive ) {
                          this.update({
                            marker: {
                              height: 36,
                              symbol: `url(${ marker })`,
                              width: 30
                            }
                          });
                        }
                      }
                    }
                  }
                },
                stickyTracking: false,
                states: {
                  hover: {
                    enabled: false
                  }
                }
              });
              let map = document.querySelector( '.map' );
              if ( !map.classList.contains( 'map--drilldown' )) {
                map.classList.add( 'map--drilldown' );
              }

              if ( map.querySelector( '.map__drillup' ) === null ) {
                buildDrillUpButton();
              }
              if ( map.querySelector( '.map__zoomin' ) === null ) {
                buildZoomInButton();
                buildZoomOutButton();
              }
            };

            if ( document.querySelector( '.map--touch' )) {
              chart.tooltip.options.enabled = false;
            }

            if ( !Highcharts.maps[ shortUrl ]) {
              window.fetch( geoJsonBaseUrl + shortUrl + '.geo.json', { 'credentials': 'same-origin' })
                .then(( res ) => res.json())
                .then(( geoJson ) => {
                  Highcharts.maps[ shortUrl ] = geoJson;
                  addDrilldownMap();
                })
                .catch(( error ) => {
                  console.log( error );
                });
            } else {
              addDrilldownMap();
            }
          },
          drillup: function( e ) {
            this.get( 'cities' ).remove();
            let drillupButton = document.getElementById( 'map-drillup' );
            let zoomInButton = document.getElementById( 'map-zoomin' );
            let zoomOutButton = document.getElementById( 'map-zoomout' );
            let locationListContinent = document.getElementById( 'map-location-list-continent' );
            let backButton = document.querySelector( '.map__location-list-back-button-container' );
            let locationInfo = document.getElementById( 'map-location' );
            if ( locationInfo !== null ) {
              locationInfo.parentNode.removeChild( locationInfo );
              handleResize();
            }

            if ( locationInfo !== null ) {
              setTimeout(() => {
                if ( !backButton.classList.contains( '.map__location-list-back-button-container--hidden' )) {
                  backButton.children[ 0 ].click();
                }

                let map = document.querySelector( '.map' );
                if ( map.classList.contains( 'map--drilldown' )) {
                  map.classList.remove( 'map--drilldown' );
                }

                drillupButton.parentNode.removeChild( drillupButton );
                zoomInButton.parentNode.removeChild( zoomInButton );
                zoomOutButton.parentNode.removeChild( zoomOutButton );
                if ( locationListContinent !== null ) {
                  locationListContinent.parentNode.removeChild( locationListContinent );
                }

                handleResize();
              }, 0 );
            } else {
              if ( !backButton.classList.contains( '.map__location-list-back-button-container--hidden' )) {
                backButton.children[ 0 ].click();
              }

              let map = document.querySelector( '.map' );
              if ( map.classList.contains( 'map--drilldown' )) {
                map.classList.remove( 'map--drilldown' );
              }

              drillupButton.parentNode.removeChild( drillupButton );
              zoomInButton.parentNode.removeChild( zoomInButton );
              zoomOutButton.parentNode.removeChild( zoomOutButton );
              if ( locationListContinent !== null ) {
                locationListContinent.parentNode.removeChild( locationListContinent );
              }

              handleResize();
            }
          }
        }
      },
      title: {
        text: ''
      },
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderColor: '#DBE5F0',
        enabled: true,
        headerFormat: '',
        shadow: false,
        style: {
          color: '#004B95',
          fontFamily: '\'TheSansC5-5\', sans-serif'
        },
        formatter: function() {
          let locations = '';

          if ( this.point.value > 1 ) {
            locations = lang.translations.locations;
          } else {
            locations = lang.translations.location;
          }

          if ( this.point.tooltip ) {
            if ( typeof this.point.marker === 'undefined' ) {
              return '<div class="map__tooltip" style="text-align: center"><b>' + this.point.labelName + '</b><br><span>' + this.point.value + ' ' + locations + '</span></div>';
            } else {
              return '<div class="map__tooltip" style="text-align: center"><b>' + this.point.name + '</b></div>';
            }
          } else {
            return false;
          }
        }
      },

      plotOptions: {
        series: {
          dragDrop: {
            draggableX: true,
            draggableY: true
          },
          marker: {
            states: {
              hover: {
                enabled: false
              }
            }
          }
        }
      },

      mapNavigation: {
        enabled: true,
        enableMouseWheelZoom: false,
        buttonOptions: {
          verticalAlign: 'bottom'
        },
        buttons: {
          zoomIn: {
            style: {
              backgroundColor: '#FFFFFF',
              color: '#004B95'
            }
          }
        }
      },

      series: [],
      drilldown: {
        activeDataLabelStyle: {
          color: '#FFFFFF',
          textDecoration: 'none',
          textOutline: '1px #000000'
        },
        drillUpButton: {
          relativeTo: 'spacingBox',
          position: {
            x: 0,
            y: 60
          }
        }
      }
    });

    function windowResize() {
      window.addEventListener( 'resize', Helper.debounce(() => {
        handleResize();
      }, 250 ));
    }

    let handleResize = function() {
      let width = 'auto';
      let desktopMinWidth = 1024;
      let overlayMaxWidth = 612;
      let overlayWidth = overlayMaxWidth;
      let hasOverlay = document.querySelector( '.map__location-container--show' );
      let rect = this.chartContainer.getBoundingClientRect();

      // on desktop the overlay is next to chart
      if ( hasOverlay && window.innerWidth >= desktopMinWidth ) {
        if ( window.innerWidth < overlayWidth * 2 ) {
          overlayWidth = Math.min( rect.width / 2, overlayMaxWidth );
        }
        width = `${Math.max( rect.width - overlayWidth, chart.chartHeight )}px`;
      } else if ( window.innerWidth < desktopMinWidth ) {
        width = '100%';
      }

      if ( document.querySelector( '.map--touch' )) {
        chart.tooltip.options.enabled = false;
      }

      // if width or height is null the chart will take
      // the available space from the parent container
      // this is what we want for mobile
      this.chartContainer.style.width = width;
      this.chartContainer.style.maxWidth = width === 'auto' ? 'none' : width;
      chart.setSize( null, null );
    };

    handleResize = handleResize.bind( this );

    function buildDrillUpButton() {
      let map = document.querySelector( '.map' );
      let drillupButton = document.createElement( 'div' );
      drillupButton.classList.add( 'map__drillup' );
      drillupButton.id = 'map-drillup';
      drillupButton.innerHTML = '<svg class="svg-icon svg-icon--{{ name }}"><use xlink:href="#map_pfeil"></use></svg>';
      setTimeout(() => {
        drillupButton.classList.add( 'map__drillup--show' );
      }, 600 );
      drillupButton.addEventListener( 'click', () => {
        backToWorldMap();
      });
      map.appendChild( drillupButton );
    }

    function backToWorldMap() {
      chart.drillUp();
    }

    function buildZoomInButton() {
      let map = document.querySelector( '.map' );
      let zoomInButton = document.createElement( 'div' );

      if ( map.querySelector( '.map__zoomin' ) === null ) {
        zoomInButton.classList.add( 'map__zoomin' );
        zoomInButton.id = 'map-zoomin';
        zoomInButton.innerHTML = '<svg class="svg-icon svg-icon--{{ name }}"><use xlink:href="#map_minus"></use></svg>';
        setTimeout(() => {
          zoomInButton.classList.add( 'map__zoomin--show' );
        }, 600 );
        zoomInButton.addEventListener( 'click', () => {
          chart.mapZoom( 1.2, chart.xAxis[ 0 ].toValue( 350 ), chart.yAxis[ 0 ].toValue( 480 ), 350, 480 );
        });
        map.appendChild( zoomInButton );
      }
    }

    function buildZoomOutButton() {
      let map = document.querySelector( '.map' );
      let zoomOutButton = document.createElement( 'div' );

      if ( map.querySelector( '.map__zoomout' ) === null ) {
        zoomOutButton.classList.add( 'map__zoomout' );
        zoomOutButton.id = 'map-zoomout';
        zoomOutButton.innerHTML = '<svg class="svg-icon svg-icon--{{ name }}"><use xlink:href="#map_plus"></use></svg>';
        setTimeout(() => {
          zoomOutButton.classList.add( 'map__zoomout--show' );
        }, 600 );
        zoomOutButton.addEventListener( 'click', () => {
          chart.mapZoom( .8, chart.xAxis[ 0 ].toValue( 350 ), chart.yAxis[ 0 ].toValue( 480 ), 350, 480 );
        });
        map.appendChild( zoomOutButton );
      }
    }

    let pointWithLatLon = function( point, latLon ) {
      return Highcharts.merge( point, chart.transformFromLatLon( latLon,
        Highcharts.maps[ 'custom/world-continents' ][ 'hc-transform' ][ 'default' ]));
    };
    let pointWithLatLonCountry = function( point, latLon ) {
      return Highcharts.merge( point, chart.transformFromLatLon( latLon,
        Highcharts.maps[ 'custom/world-palestine-highres' ][ 'hc-transform' ][ 'default' ]));
    };

    let worldViewCities = [];
    for ( let location of longLatsData ) {
      worldViewCities.push( pointWithLatLon({ name: location.name }, { lat: location.latitude, lon: location.longitude }));
    }
    let countryViewCities = [];
    for ( let location of longLatsData ) {
      countryViewCities.push( pointWithLatLonCountry({ name: location.name }, { lat: location.latitude, lon: location.longitude }));
    }

    if ( worldViewCities.length > 0 && countryViewCities.length > 0 ) {
      worker.postMessage([ worldData, worldViewCities, countryData, countryViewCities, lang ]);
    }

    let continents = [];
    worker.onmessage = function( e ) {
      worldData = e.data[ 0 ];
      countryData = e.data[ 1 ];
      continents = e.data[ 2 ];
      countryViewCities = e.data[ 3 ];

      chart.addSeries({
        id: 'mubea',
        name: 'Mubea',
        data: countryData,
        dataLabels: {
          enabled: true,
          format: '{point.properties.postal-code}'
        },
        states: {
          hover: {
            enabled: false
          }
        }
      });
      chart.addSeries({
        id: 'mubea-europe',
        name: 'Mubea Europe',
        data: continents,
        joinBy: 'hc-key',
        allAreas: false,
        dataLabels: {
          enabled: true,
          format: '{point.properties.postal-code}'
        },
        states: {
          hover: {
            color: '#0096C1'
          }
        }
      });

      chart.addSeries({
        id: 'citiesWorld',
        name: 'Cities World',
        type: 'mappoint',
        color: 'white',
        marker: {
          symbol: 'point',
          lineColor: '#004B95',
          lineWidth: 1
        },
        data: worldViewCities,
        dataLabels: {
          enabled: false
        }
      });

      mapLocation.createMobileOverview( 'world', continents, lang, chart );
    };

    function checkLangCountry( itemCountry ) {
      for ( let country of lang.countries ) {
        if ( itemCountry === country.name ) {
          return country.translation;
        }
      }
      return itemCountry;
    }

    windowResize();
  }
}
